import React from "react";
import classNames from "classnames";
import { Spinner } from "@/components/ui/feedback/Spinner";
import styles from "./Button.module.css";

export type ButtonShape = "default" | "pill" | "square";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Button variant style */
  variant?: "primary" | "secondary" | "outline" | "ghost";
  /** Button size */
  size?: "sm" | "md" | "lg";
  /**
   * Button shape
   * - default: Standard rounded corners
   * - pill: Fully rounded ends (capsule shape)
   * - square: No border radius
   * @default 'default'
   */
  shape?: ButtonShape;
  /** Full width button */
  fullWidth?: boolean;
  /** Loading state */
  isLoading?: boolean;
  /** Text to display when loading (defaults to children) */
  loadingText?: string;
  /** Children content */
  children: React.ReactNode;
}

/**
 * Primary UI component for user interaction
 *
 * @example
 * ```tsx
 * <Button variant="primary" size="md">Click me</Button>
 * ```
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      shape = "default",
      fullWidth = false,
      isLoading = false,
      loadingText,
      disabled,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    // Display text when loading: loadingText > children
    const loadingContent = loadingText ?? children;
    return (
      <button
        ref={ref}
        disabled={disabled || isLoading}
        className={classNames(
          styles.button,
          styles[variant],
          styles[size],
          {
            [styles.fullWidth]: fullWidth,
            [styles.pill]: shape === "pill",
            [styles.square]: shape === "square",
          },
          className,
        )}
        {...props}
      >
        {isLoading ? (
          <>
            <Spinner size="sm" variant="inherit" className={styles.spinner} />
            {loadingContent}
          </>
        ) : (
          children
        )}
      </button>
    );
  },
);

Button.displayName = "Button";
